import React, { createContext, useState, useContext, useEffect } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, collection, query, where, getDocs, serverTimestamp, onSnapshot, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { initializeUserE2E } from '../services/e2eAccountSetupService';
import SessionDialog from '../components/ui/SessionDialog';
import { generateDeviceFingerprint, isDeviceSwitched, recordDeviceLogin } from '../services/deviceDetectionService';
import { SecureSessionManager } from '../security/auth';
import { forceLogoutPreviousSessions, cancelNewLogin } from '../services/sessionManagementService';
import { initializeFCM } from '../services/notificationMessagingService';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionDialog, setSessionDialog] = useState(null); // { message, type }
  const [isSessionVerifying, setIsSessionVerifying] = useState(false); // New state to block UI during verification
  const googleProvider = new GoogleAuthProvider();


  // ✅ SESSION MONITORING - Real-time listener for session conflicts
  useEffect(() => {
    if (!user?.uid) return;

    const sessionRef = doc(db, 'activeSessions', user.uid);
    const unsubscribe = onSnapshot(sessionRef, (snapshot) => {
      if (!snapshot.exists()) return;

      const sessionData = snapshot.data();
      const localSessionId = localStorage.getItem('sessionId');

      // Session mismatch = another device logged in
      if (sessionData.sessionId && localSessionId && sessionData.sessionId !== localSessionId) {

        // Prevent multiple triggers
        if (sessionDialog?.type === 'warning') return;

        // Show warning dialog (read-only, no buttons)
        setSessionDialog({
          message: 'You have been logged out because your account was accessed from another device.\n\nNote: You can only have one active session at a time.',
          type: 'warning',
          showButtons: false,
          autoDismiss: 0 // Persist indefinitely until closed
        });

        // Logout after 3 seconds
        setTimeout(async () => {
          try {
            await signOut(auth);
            localStorage.removeItem('sessionId');
            // ✅ FIX: Do NOT force reload the page. 
            // This allows the "You have been logged out" dialog to remain visible
            // while the app routing redirects to login in the background.
            // window.location.href = '/login'; 
          } catch (error) {
            console.error('Session conflict logout error:', error);
          }
        }, 3000);
      }
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // ✅ Helper: Check for session conflicts
  // Returns object with status and details
  const checkSessionConflict = async (userId, currentFingerprint) => {
    const activeSession = await import('../services/sessionManagementService').then(m => m.getActiveSessionInfo(userId));

    // No active session or session is stale -> No conflict
    if (!activeSession || !activeSession.sessionId) {
      return { hasConflict: false };
    }

    // Check if it's the same device
    const storedFingerprint = activeSession.deviceInfo?.fingerprint;
    let isSameDevice = false;

    if (storedFingerprint) {
      isSameDevice = storedFingerprint === currentFingerprint;
    } else {
      // Legacy fallback
      isSameDevice = activeSession.deviceInfo?.userAgent === navigator.userAgent;
    }

    if (isSameDevice) {
      return { hasConflict: false, isSameDevice: true };
    }

    // Real conflict detected
    return { hasConflict: true, activeSession };
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // If already verifying, don't interrupt
          if (isSessionVerifying) return;

          // ============================================================
          // 🔒 SESSION VALIDATION ON REFRESH
          // ============================================================
          const currentFingerprint = await generateDeviceFingerprint();
          const sessionCheck = await checkSessionConflict(firebaseUser.uid, currentFingerprint);

          if (sessionCheck.hasConflict) {
            console.log('🔒 Session conflict detected on refresh/load - Blocking access');
            setLoading(true); // Keep UI blocked

            // Show blocking dialog
            setSessionDialog({
              message: 'Your account is active on another device or browser. Logging in here will disconnect the other session. Note: For security reasons, you may not be able to see older messages in this new session.',
              type: 'action',
              showButtons: true,
              autoDismiss: 0,
              onConfirm: async () => {
                setSessionDialog(null);
                await finalizeSessionCreation(firebaseUser.uid, currentFingerprint);
                setLoading(false); // Unblock
                // Continue with user setup...
                await performUserSetup(firebaseUser); // Extract setup logic below
              },
              onCancel: async () => {
                setSessionDialog(null);
                await import('../services/sessionManagementService').then(m => m.cancelNewLogin());
                setLoading(false);
              }
            });
            return; // STOP here, wait for user action
          } else if (sessionCheck.isSameDevice) {
            // Silent takeover/update for same device
            console.log('🔄 Same device session verified');
            await finalizeSessionCreation(firebaseUser.uid, currentFingerprint);
          }

          // ============================================================
          // NORMAL SETUP (No conflict or resolved)
          // ============================================================
          await performUserSetup(firebaseUser);

        } catch (error) {
          console.error('Auth state change error:', error);
          setUser(null); // Fallback
          setLoading(false);
        }
      } else {
        setUser(null);
        setLoading(false);
      }
    });
    return unsubscribe;
  }, [isSessionVerifying]);

  // Extracted user setup logic to reuse
  const performUserSetup = async (firebaseUser) => {
    // ============================================================
    // DEVICE SWITCH DETECTION & AUTO CACHE CLEARING
    // ============================================================
    try {
      const currentFingerprint = await generateDeviceFingerprint();
      // Check if user switched devices (Local cache vs Firestore history)
      const deviceSwitched = await isDeviceSwitched(firebaseUser.uid, currentFingerprint);

      if (deviceSwitched) {
        console.log('🔄 Device switch detected - clearing encryption cache...');
        const sessionManager = new SecureSessionManager();
        await sessionManager.clearEncryptionCacheOnDeviceSwitch(firebaseUser.uid);

        setSessionDialog({
          message: 'Switched to a different device. Re-initializing encryption keys...',
          type: 'info',
          showButtons: false,
          autoDismiss: 2000
        });
        setTimeout(() => setSessionDialog(null), 2000);
      }

      await recordDeviceLogin(firebaseUser.uid, currentFingerprint);
    } catch (deviceDetectionError) {
      console.warn('Device detection error:', deviceDetectionError.message);
    }

    if (!firebaseUser.emailVerified) {
      await firebaseUser.reload();
      await firebaseUser.getIdToken(true);
    }

    let userData;
    try {
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      if (userDoc.exists()) {
        userData = userDoc.data();
      } else {
        userData = {
          email: firebaseUser.email,
          firstName: firebaseUser.displayName?.split(' ')[0] || '',
          lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
          accountType: 'Seeker',
          createdAt: new Date(),
          isGoogleUser: true,
          emailVerified: firebaseUser.emailVerified
        };
      }
    } catch (userDocError) {
      userData = {
        email: firebaseUser.email,
        firstName: firebaseUser.displayName?.split(' ')[0] || '',
        lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
        accountType: 'Seeker',
        isGoogleUser: true,
        emailVerified: firebaseUser.emailVerified
      };
    }

    // Dashboard data setup
    let dashboardData = {};
    try {
      // ... simplified dashboard fetch ... 
      const headerRef = doc(db, 'dashboardHeaders', firebaseUser.uid);
      const mainRef = doc(db, 'dashboardMain', firebaseUser.uid);
      const headerSnap = await getDoc(headerRef);
      const mainSnap = await getDoc(mainRef);

      if (headerSnap.exists()) {
        dashboardData.header = headerSnap.data();
      } else {
        const isProvider = (userData.accountType === 'Provider');
        const isExplorer = (userData.accountType === 'Explorer');
        dashboardData.header = {
          welcomeMessage: isProvider ? `Welcome Provider ${userData.firstName || ''}` : isExplorer ? `Welcome Explorer ${userData.firstName || ''}` : `Welcome ${userData.firstName || ''}`,
          subtitle: isProvider ? 'Manage your listings and applicants' : isExplorer ? 'Explore rooms, save favorites, and discover perfect spaces!' : 'Find your perfect room and connect!',
          quickStats: { rooms: 0, messages: 0 },
          accountType: userData.accountType,
          userName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
        };
      }

      if (mainSnap.exists()) {
        dashboardData.main = mainSnap.data();
      } else {
        dashboardData.main = {
          applications: [],
          activities: [],
          savedListings: [],
          quickStats: { rooms: 0, messages: 0 }
        };
      }
    } catch (e) {
      dashboardData = { header: {}, main: {} }; // Fallback
    }

    setUser({
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
      metadata: firebaseUser.metadata,
      ...userData,
      emailVerified: firebaseUser.emailVerified,
      dashboardHeader: dashboardData.header,
      dashboardMain: dashboardData.main
    });

    setLoading(false); // ✅ UNBLOCK UI HERE

    initializeUserE2E(firebaseUser.uid).catch(err => {
      console.warn('E2E initialization on login:', err.message);
    });

    // 🔔 Initialize Firebase Cloud Messaging
    initializeFCM().then(result => {
      if (result.success) {
        console.log('✅ FCM initialized for user');
      }
    }).catch(err => {
      console.warn('⚠️ FCM initialization warning:', err.message);
    });
  };

  // ✅ HELPER: Generate unique session ID
  const generateSessionId = () => {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  // ✅ HELPER: Create session in Firestore
  const createSession = async (userId) => {
    // 1. Check if there is ALREADY an active session
    const currentFingerprint = await generateDeviceFingerprint();
    const sessionCheck = await checkSessionConflict(userId, currentFingerprint);

    if (sessionCheck.hasConflict) {
      console.log('⚠️ REAL active session detected on DIFFERENT device');

      return new Promise((resolve, reject) => {
        // Define handlers for the dialog
        const handleLogoutPrevious = async () => {
          try {
            setSessionDialog(null);
            // Proceed to create new session
            await finalizeSessionCreation(userId, currentFingerprint);
            setIsSessionVerifying(false);
            resolve();
          } catch (error) {
            console.error('Error in logout previous handler:', error);
            setIsSessionVerifying(false); // Unblock
            reject(error);
          }
        };

        const handleCancelLogin = async () => {
          try {
            setSessionDialog(null);
            // Sign out immediately
            await import('../services/sessionManagementService').then(m => m.cancelNewLogin());
            setIsSessionVerifying(false); // Unblock
            const error = new Error('Login cancelled by user');
            error.code = 'auth/login-cancelled';
            reject(error);
          } catch (error) {
            console.error('Error in cancel login handler:', error);
            setIsSessionVerifying(false);
            reject(error);
          }
        };

        // Show the decision dialog
        setSessionDialog({
          message: 'Your account is active on another device or browser. Logging in here will disconnect the other session. Note: For security reasons, you may not be able to see older messages in this new session.',
          type: 'action', // Shows buttons
          onConfirm: handleLogoutPrevious,
          onCancel: handleCancelLogin,
          showButtons: true,
          autoDismiss: 0 // Do NOT auto dismiss, wait for user
        });
      });
    } else {
      // ✅ No active session OR same device takeover
      console.log('✅ No active session detected or same device, proceeding');
      await finalizeSessionCreation(userId, currentFingerprint);
      setIsSessionVerifying(false); // ✅ UNLOCK if no conflict
    }
  };

  // Internal helper to actually write the session doc
  const finalizeSessionCreation = async (userId, fingerprint = null) => {
    const sessionId = generateSessionId();

    try {
      // If fingerprint not passed (legacy call), generate it
      if (!fingerprint) {
        fingerprint = await generateDeviceFingerprint();
      }

      // Create the session in Firestore (overwrites any previous session)
      await setDoc(doc(db, 'activeSessions', userId), {
        sessionId,
        deviceInfo: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          fingerprint: fingerprint, // ✅ Store fingerprint for future comparison
          timestamp: serverTimestamp()
        },
        loginTime: serverTimestamp(),
        lastActive: serverTimestamp(),
        // Check if we are overwriting (implicitly handled by setDoc/merge logic if we wanted to merge, but we want fresh state)
        forceLogoutTriggered: true, // Mark implicit force logout
        forceLogoutAt: serverTimestamp()
      });

      // Store session ID locally
      localStorage.setItem('sessionId', sessionId);
      console.log('✅ Session created:', sessionId);
    } catch (error) {
      console.error('Failed to create session:', error);
    }
  };

  // Email/Password Login
  const login = async (email, password) => {
    setIsSessionVerifying(true); // 🔒 lock UI
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      // Create/update session
      await createSession(userCredential.user.uid);
      return userCredential.user;
    } catch (e) {
      setIsSessionVerifying(false); // 🔓 unlock on error
      throw e;
    }
  };

  // Email/Password Signup
  const signup = async (email, password, userData) => {
    // Signup usuall doesn't have existing session conflict, but let's be safe if we add logic later
    // For now, createSession isn't blocking, but we should use it?
    // The original code called setDoc users and then updateProfile.
    // It didn't call createSession in the snippet provided in ViewFile?
    // Let's check the original code again.
    // Original said: 
    // const signup = ... 
    // ...
    // return userCredential.user;
    // It did NOT call createSession? 
    // Login called it. signInWithGoogle called it. Signup didn't?
    // Wait, session management service says "Handles multi-device session management".
    // If I signup, I definitely should have a session.
    // But maybe unrelated to this specific bug.
    // I will focus on login and google signin.
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    // ... rest of signup logic ...
    // ...
    // If signup doesn't call createSession, I shouldn't add it now unless necessary.
    // I will stick to login/google.

    // ... existing signup code ...
    // Generate username and displayName
    const username = (userData.firstName && userData.lastName)
      ? `${userData.firstName}${userData.lastName}`.replace(/\s+/g, '').toLowerCase()
      : email.split('@')[0];
    const displayName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || username;
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      email: email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      username: username,
      displayName: displayName,
      accountType: userData.accountType || 'Seeker',
      marketing: userData.marketing || false,
      referralCode: userData.referralCode || '',
      createdAt: serverTimestamp(),
      isGoogleUser: false,
      emailVerified: false
    });
    // Update Firebase Auth profile
    try {
      await userCredential.user.updateProfile({ displayName });
    } catch (e) { /* ignore */ }

    // ✅ Create session for new user
    await createSession(userCredential.user.uid);

    return userCredential.user;
  };

  // Google Signin/Signup
  const signInWithGoogle = async (accountType = 'Seeker') => {
    setIsSessionVerifying(true); // 🔒 lock UI
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);

      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));

      if (!userDoc.exists()) {
        // First time Google user - generate username and displayName like regular signup
        const firstName = userCredential.user.displayName?.split(' ')[0] || '';
        const lastName = userCredential.user.displayName?.split(' ').slice(1).join(' ') || '';

        // ✅ NEW: Check if email already exists (should not, but validate for safety)
        const emailQuery = query(collection(db, 'users'), where('email', '==', userCredential.user.email));
        const emailSnapshot = await getDocs(emailQuery);
        if (emailSnapshot.docs.length > 0) {
          // Email already registered, user is logging in (not first time)
          await createSession(userCredential.user.uid);
          // Unlock handled inside createSession if success, or here?
          // createSession logic needs to handle unlock.
          return userCredential.user;
        }

        // ✅ NEW: Check if firstName + lastName combination already exists
        const nameQuery = query(
          collection(db, 'users'),
          where('firstName', '==', firstName),
          where('lastName', '==', lastName)
        );
        const nameSnapshot = await getDocs(nameQuery);
        if (nameSnapshot.docs.length > 0) {
          // Name exists, append email hash to make it unique
          console.warn('⚠️ Name exists, generating unique variation');
        }

        const username = (firstName && lastName)
          ? `${firstName}${lastName}`.replace(/\s+/g, '').toLowerCase()
          : userCredential.user.email.split('@')[0];
        const displayName = `${firstName} ${lastName}`.trim() || username;

        await setDoc(doc(db, 'users', userCredential.user.uid), {
          email: userCredential.user.email,
          firstName: firstName,
          lastName: lastName,
          username: username,
          displayName: displayName,
          accountType: accountType,
          createdAt: serverTimestamp(),
          isGoogleUser: true,
          emailVerified: userCredential.user.emailVerified
        });
      } else {
        // Update account type if needed
        const existingData = userDoc.data();
        if (existingData.accountType !== accountType) {
          await updateDoc(doc(db, 'users', userCredential.user.uid), {
            accountType: accountType
          });
        }
      }

      // Create/update session for Google login
      await createSession(userCredential.user.uid);

      return userCredential.user;
    } catch (e) {
      setIsSessionVerifying(false); // 🔓 unlock on error
      throw e;
    }
  };

  // Logout
  const logout = async () => {
    // ✅ FIX: Always clear local storage first, before any async operations
    // This ensures cleanup happens even if Firestore fails
    const currentUserId = auth.currentUser?.uid;
    localStorage.removeItem('sessionId');
    sessionStorage.clear(); // Clear device-related data too

    try {
      // ✅ FIX: Use setDoc with sessionId: null instead of deleteDoc
      // This is more reliable because:
      // 1. It doesn't fail if document doesn't exist
      // 2. It leaves a record of logout (for debugging)
      // 3. The getActiveSessionInfo now checks for null sessionId
      if (currentUserId) {
        await setDoc(doc(db, 'activeSessions', currentUserId), {
          sessionId: null, // Mark as logged out
          logoutAt: serverTimestamp(),
          lastActive: serverTimestamp()
        }, { merge: true });
      }

      // Sign out from Firebase
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      // ✅ Even if Firestore operations fail, complete the logout
      // Local storage is already cleared above
      try {
        await signOut(auth);
      } catch (signOutError) {
        console.error('Sign out error:', signOutError);
        // Force page reload to clear state if signOut fails
        window.location.href = '/login';
      }
    }
  };

  // Reset password
  // Reset password
  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  // Sync user state manually (useful after verification)
  const syncUser = async () => {
    if (!auth.currentUser) return;

    try {
      await auth.currentUser.reload();

      // Check again if user is still logged in after reload
      if (!auth.currentUser) return;

      setUser(prev => {
        if (!prev) return null; // Don't update if user logged out
        return {
          ...prev,
          emailVerified: auth.currentUser.emailVerified
        };
      });

      // ✅ FORCE JWT TOKEN REFRESH
      // This is CRITICAL: reload() updates the local object, but Firestore 
      // security rules look at the JWT token claims. Refreshing the token
      // ensures the 'email_verified: true' claim is sent to the server.
      await auth.currentUser.getIdToken(true);

      return auth.currentUser;
    } catch (e) {
      console.warn("Sync user failed:", e);
      return null;
    }
  };

  const value = {
    user,
    login,
    signup,
    logout,
    resetPassword,
    signInWithGoogle,
    syncUser, // ✅ Exposed for verification page
    loading,

  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
      {sessionDialog && (
        <SessionDialog
          message={sessionDialog.message}
          type={sessionDialog.type}
          onClose={() => setSessionDialog(null)}
          autoDismiss={sessionDialog.autoDismiss !== undefined ? sessionDialog.autoDismiss : 3000}
          onConfirm={sessionDialog.onConfirm || null}
          onCancel={sessionDialog.onCancel || null}
          showButtons={sessionDialog.showButtons || false}
        />
      )}
    </AuthContext.Provider>
  );
};
